import { useState, useEffect, useMemo } from 'react';
import LoadBar from '../shared/LoadBar';
import api from '../../hooks/useApi';
import {
  toUTC, toISO, addDays, daysBetween, dow, fmtD, fmtDY,
} from '../commercial/plan/planConstants';

/*
  Every active plan on one timeline, and everybody's committed time across all
  of them.

  A single SOW's plan can never answer the question a delivery lead actually
  has, which is not "when does this project finish" but "can the team deliver
  what we have already sold". Contention is between projects, so it can only be
  seen from above them.
*/

const ROW_H = 26;
const HDR_H = 34;
const LEFT_W = 300;

const money = (n) => (n ? `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');

export default function PlanPortfolioPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('timeline');
  const [months, setMonths] = useState(4);

  useEffect(() => {
    setData(null);
    const to = addDays(new Date().toISOString().slice(0, 10), months * 30);
    api.get('plan-portfolio', { to })
      .then(setData)
      .catch(e => setError(e.message || 'Could not load the portfolio.'));
  }, [months]);

  const range = useMemo(() => {
    if (!data?.projects?.length) return null;
    const starts = data.projects.map(p => p.plan_start).filter(Boolean).sort();
    const ends = data.projects.map(p => p.plan_finish).filter(Boolean).sort();
    if (!starts.length) return null;
    const from = addDays(starts[0], -7);
    return { from, to: addDays(ends[ends.length - 1], 14) };
  }, [data]);

  const totalDays = range ? (daysBetween(range.from, range.to) || 1) + 1 : 1;
  const pxPerDay = Math.max(0.6, Math.min(6, 1100 / totalDays));
  const totalW = Math.round(totalDays * pxPerDay);
  const xOf = (d) => (d ? Math.round((daysBetween(range.from, d) || 0) * pxPerDay) : 0);

  const monthTicks = useMemo(() => {
    if (!range) return [];
    const out = [];
    const d = new Date(toUTC(range.from));
    d.setUTCDate(1);
    while (d.getTime() <= toUTC(range.to)) {
      out.push(toISO(d.getTime()));
      d.setUTCMonth(d.getUTCMonth() + 1);
    }
    return out;
  }, [range]);

  const overloaded = (data?.people || []).filter(p => p.over_weeks > 0);

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-[15px] font-bold text-navy">Plan portfolio</h1>
        <div className="flex rounded border border-gray-200 overflow-hidden">
          {[['timeline', 'Timeline'], ['resources', 'Resources'], ['health', 'Health']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
                    className={`px-2.5 py-1 text-[10px] font-semibold ${tab === id ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <select value={months} onChange={e => setMonths(Number(e.target.value))}
                className="text-[10px] border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-600">
          {[2, 4, 6, 12].map(m => <option key={m} value={m}>Next {m} months</option>)}
        </select>
        {data && (
          <span className="text-[10px] text-gray-500 ml-auto">
            {data.projects.length} active plan{data.projects.length !== 1 ? 's' : ''}
            {overloaded.length > 0 && (
              <span className="text-red-600 font-semibold"> · {overloaded.length} over-committed</span>
            )}
          </span>
        )}
      </div>

      {error && <div className="text-[10px] text-red-600 bg-red-50 px-3 py-2 rounded">{error}</div>}
      {!data && !error && <div className="py-12 text-center text-[11px] text-gray-400">Loading…</div>}

      {data && data.projects.length === 0 && (
        <div className="bg-white border border-gray-200 rounded p-8 text-center text-[11px] text-gray-400">
          No active SOW has a dated plan yet. A plan appears here once its tasks have dates.
        </div>
      )}

      {/* ── Every plan on one timeline ── */}
      {data && tab === 'timeline' && range && (
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <div className="flex overflow-x-auto">
            <div className="flex-shrink-0 border-r border-gray-200" style={{ width: LEFT_W }}>
              <div className="bg-gray-50 border-b border-gray-200 px-2 flex items-end" style={{ height: HDR_H }}>
                <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400 pb-1">SOW</span>
              </div>
              {data.projects.map(p => (
                <div key={p.id} className="flex items-center gap-1.5 px-2 border-b border-gray-50"
                     style={{ height: ROW_H }}>
                  <span className="text-[9px] font-semibold text-navy truncate w-20 flex-shrink-0">{p.reference}</span>
                  <span className="text-[8px] text-gray-500 truncate flex-1">{p.client_name || p.detail}</span>
                  <span className="text-[8px] tabular-nums w-7 text-right"
                        style={{ color: p.late_count > 0 ? '#DC2626' : '#6B7280' }}>{p.percent_complete}%</span>
                </div>
              ))}
            </div>

            <div className="flex-1 min-w-0 overflow-x-auto">
              <svg width={totalW} height={HDR_H + data.projects.length * ROW_H} style={{ display: 'block' }}>
                <rect x="0" y="0" width={totalW} height={HDR_H} fill="#F9FAFB" />
                {monthTicks.map(m => (
                  <g key={m}>
                    <line x1={xOf(m)} y1="0" x2={xOf(m)} y2={HDR_H + data.projects.length * ROW_H}
                          stroke="#E5E7EB" strokeWidth="1" />
                    <text x={xOf(m) + 3} y={HDR_H - 10} fontSize="8" fill="#6B7280" fontWeight="700"
                          fontFamily="Arial, sans-serif">
                      {new Date(toUTC(m)).toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' })}
                    </text>
                  </g>
                ))}
                {data.projects.map((p, i) => {
                  const x = xOf(p.plan_start);
                  const w = Math.max(3, xOf(p.plan_finish) - x);
                  const y = HDR_H + i * ROW_H + 6;
                  const colour = p.late_count > 0 ? '#DC2626' : p.percent_complete >= 100 ? '#10B981' : '#0EA5E9';
                  return (
                    <g key={p.id}>
                      <rect x={x} y={y} width={w} height={13} rx="2.5" fill={colour} fillOpacity="0.85" />
                      <rect x={x} y={y + 3.5} width={Math.max(1, (w * p.percent_complete) / 100)} height={6}
                            rx="1.5" fill="#0F172A" fillOpacity="0.4" />
                      <title>
                        {`${p.reference}\n${fmtDY(p.plan_start)} to ${fmtDY(p.plan_finish)}\n${p.percent_complete}% complete\n${p.late_count} late task(s)`}
                      </title>
                    </g>
                  );
                })}
                {(() => {
                  const x = xOf(data.today);
                  if (x < 0 || x > totalW) return null;
                  return (
                    <g>
                      <line x1={x} y1="0" x2={x} y2={HDR_H + data.projects.length * ROW_H}
                            stroke="#DC2626" strokeWidth="1.3" strokeDasharray="4 3" />
                      <text x={x + 3} y="10" fontSize="7.5" fill="#DC2626" fontWeight="700"
                            fontFamily="Arial, sans-serif">TODAY</text>
                    </g>
                  );
                })()}
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* ── Everybody, across everything ── */}
      {data && tab === 'resources' && (
        <div className="space-y-2">
          <div className="text-[9px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
            Days of work per week against each person's own capacity: contracted hours on the days they work, less
            approved leave (by the half day), England and Wales bank holidays and company or team closures. Active SOWs
            are committed work; Draft and Issued SOWs are bid work, at their win probability, shown striped.
          </div>
          {data.people.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded p-6 text-center text-[10px] text-gray-400">
              Nobody is assigned to a dated task in this window.
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-2 py-1 text-left text-[7.5px] uppercase text-gray-400 font-bold sticky left-0 bg-gray-50">Person</th>
                    {data.weeks.map(w => (
                      <th key={w} className="px-1 py-1 text-center text-[7px] text-gray-400 font-bold whitespace-nowrap">{fmtD(w)}</th>
                    ))}
                    <th className="px-2 py-1 text-right text-[7.5px] uppercase text-gray-400 font-bold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.people.map(p => (
                    <tr key={p.person_id} className="border-t border-gray-100">
                      <td className="px-2 py-1 text-[9px] font-semibold text-gray-700 whitespace-nowrap sticky left-0 bg-white">
                        {p.name}
                        {p.fte < 1 && <span className="ml-1 text-[7px] text-gray-400">{Math.round(p.fte * 100)}%</span>}
                      </td>
                      {p.weeks.map(cell => (
                        <td key={cell.week} className="p-0.5 text-center min-w-[52px]"
                            title={`${fmtDY(cell.week)}\n${cell.committed_days}d of ${cell.capacity_days}d${
                              cell.tentative_days ? `\nBid ${cell.tentative_days}d` : ''}${
                              cell.over_days ? `\nOver by ${cell.over_days}d` : ''}${
                              Object.keys(cell.by_sow).length ? `\n${Object.entries(cell.by_sow).map(([k, v]) => `${k}: ${v}d`).join('\n')}` : ''}${
                              Object.keys(cell.by_sow_tentative || {}).length ? `\n${Object.entries(cell.by_sow_tentative).map(([k, v]) => `${k} (bid): ${v}d`).join('\n')}` : ''}`}>
                          <LoadBar committed={cell.committed_days} tentative={cell.tentative_days || 0} capacity={cell.capacity_days} compact />
                        </td>
                      ))}
                      <td className="px-2 py-1 text-right text-[8px] whitespace-nowrap">
                        <span className={p.utilisation_pct > 100 ? 'text-red-600 font-bold' : 'text-gray-500'}>
                          {p.utilisation_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── The numbers, per plan ── */}
      {data && tab === 'health' && (
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['SOW', 'Client', 'Owner', 'Start', 'Finish', 'Tasks', 'Done', 'Late', 'Critical',
                  'Effort', 'Planned', 'Actual', 'Value'].map(h => (
                  <th key={h} className="px-2 py-1 text-left text-[7.5px] uppercase text-gray-400 font-bold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.projects.map(p => (
                <tr key={p.id} className={`border-b border-gray-50 hover:bg-gray-50/60 ${p.late_count > 0 ? 'bg-red-50/40' : ''}`}>
                  <td className="px-2 py-1 text-[9px] font-semibold text-navy whitespace-nowrap">{p.reference}</td>
                  <td className="px-2 py-1 text-[8px] text-gray-600 truncate max-w-[160px]">{p.client_name || '—'}</td>
                  <td className="px-2 py-1 text-[8px] text-gray-600 whitespace-nowrap">{p.owner_name || '—'}</td>
                  <td className="px-2 py-1 text-[8px] text-gray-500 whitespace-nowrap tabular-nums">{fmtD(p.plan_start)}</td>
                  <td className="px-2 py-1 text-[8px] whitespace-nowrap tabular-nums"
                      style={{ color: p.overdue ? '#DC2626' : '#6B7280' }}>{fmtD(p.plan_finish)}</td>
                  <td className="px-2 py-1 text-[8px] text-gray-600 tabular-nums">{p.task_count}</td>
                  <td className="px-2 py-1 text-[8px] text-gray-600 tabular-nums">{p.done_count}</td>
                  <td className="px-2 py-1 text-[8px] tabular-nums font-semibold"
                      style={{ color: p.late_count > 0 ? '#DC2626' : '#9CA3AF' }}>{p.late_count || '—'}</td>
                  <td className="px-2 py-1 text-[8px] text-gray-500 tabular-nums">{p.critical_count || '—'}</td>
                  <td className="px-2 py-1 text-[8px] text-gray-500 tabular-nums whitespace-nowrap">
                    {p.effort_hours ? `${Math.round(p.effort_hours)}h` : '—'}
                  </td>
                  <td className="px-2 py-1 text-[8px] text-gray-600 tabular-nums whitespace-nowrap">{money(p.planned_cost)}</td>
                  <td className="px-2 py-1 text-[8px] tabular-nums whitespace-nowrap"
                      style={{ color: p.actual_cost > p.planned_cost ? '#DC2626' : '#6B7280' }}>{money(p.actual_cost)}</td>
                  <td className="px-2 py-1 text-[8px] text-gray-600 tabular-nums whitespace-nowrap">{money(p.total_excl_vat)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-2 py-1.5 text-[7.5px] text-gray-400 border-t border-gray-100">
            Planned and actual cost are the plan's, not the SOW's: they cover assigned effort at rate card
            rates and time booked to tasks, so they will not match SOW value where hardware or pass-through is
            involved.
          </div>
        </div>
      )}
    </div>
  );
}
